import { Form, FormInstance, Input, Popconfirm, Select } from "antd";
import React, { useState, useEffect, useRef } from "react";
import EntryPermission from "components/entryPermission";
import Loading from "components/loading";
import {
  IProcessesData,
  IDocumentsData,
  IRegisterActiveItem,
  ISaleChannelData,
  CheckinOriginEnum,
  IDocumentType,
  IStepEntryPermissionStatus,
  IEntryPermissionActive,
  PlateFormatType as IPlateFormatType,
  TrailerType,
  FleetType,
  IVehicleProfileByPlate,
} from "store/lobby/lobby-model";
import { DrawerStatusEnum } from "typings/global.types";
import { BackendFleetValues, getSaleChannelFormValue, getSaleChannelId, resolveFleetFormValues } from "utils/form/saleChannel";
import { checkPlateFormatType, maxLengthPlateFormatType, validationSpecialCharacters } from "utils/form/validations";
import {
  checkWhenDocumentsIsEmpty,
  getDocumentLabel,
  searchProcessByIdSelected,
  searchProcessCorrelationIdSelectedById,
  searchSaleChannelByIdSelected,
  validationCharacters,
} from "utils/functions/";
import useAfrDocumentsFlow from "utils/hooks/useAfrDocumentsFlow";
import useTranslation from "utils/hooks/useTranslation";
import AddDocument from "../document";
import DriversInputs from "../drivers";
import AddTrailersPlates from "../trailersPlates";
import * as S from "./index.styled";
import { getTrailerTypeOptions } from "./types";

type LayoutType = Parameters<typeof Form>[0]["layout"];

export interface IPropsScreenDrawerForm {
  form: FormInstance;
  disabledFields: boolean;
  salesChannelList?: Array<ISaleChannelData>;
  inboundProcessesList: Array<IProcessesData>;
  hasSaleChannel: boolean;
  visibleDrawerView: boolean;
  visibleDrawerAddNewRegister: boolean;
  documentsList: Array<IDocumentsData>;
  registerActive?: IRegisterActiveItem;
  checkinRegisterExisting?: IRegisterActiveItem;
  carrierRef: any;
  disabledPlate?: boolean;
  visibleDrawerViewMode: DrawerStatusEnum;
  drawerIsLoading?: boolean;
  hasEntryPermission: boolean;
  documentType: IDocumentType;
  isLoadingEntryPermission?: boolean;
  stepEntryPermissionStatus?: IStepEntryPermissionStatus;
  entryPermissionActive?: IEntryPermissionActive;
  saleChannelActive?: number | null;
  plateFormatType: IPlateFormatType;
  vehicleProfileByPlate?: IVehicleProfileByPlate | null;
  useVehicleProfileEndpoint?: boolean;
}

export interface IDispatchProps {
  lobbyDocumentsFetchAsync: (...args) => void;
  lobbyProcessActive: (...args) => void;
  lobbySaleChannelActive: (...args) => void;
  lobbyFetchItemCheckinByVehiclePlateAsync?: (...args) => void;
  lobbyFetchVehicleProfileByPlateAsync?: (...args) => void;
  lobbyUpdateVehicleProfileByPlate?: (...args) => void;
  lobbyFetchEntryPermissionAsync: (...args) => void;
  lobbyUpdateStepEntryPermissionStatus: (...args) => void;
  lobbyUpdateEntryPermissionActive: (...args) => void;
}

export type IDrawerForm = IPropsScreenDrawerForm & IDispatchProps;

interface IShouldShowAutoFillParams {
  useVehicleProfileEndpoint?: boolean;
  visibleDrawerAddNewRegister: boolean;
  plate: string;
  validationPlate: RegExp;
  checkinRegisterExisting?: IRegisterActiveItem;
}

const shouldShowAutoFill = ({ useVehicleProfileEndpoint, visibleDrawerAddNewRegister, plate, validationPlate, checkinRegisterExisting }: IShouldShowAutoFillParams): boolean =>
  !useVehicleProfileEndpoint && visibleDrawerAddNewRegister && validationPlate.test(plate) && checkinRegisterExisting !== null;

const DrawerForm = ({
  form,
  disabledPlate,
  carrierRef,
  registerActive,
  checkinRegisterExisting,
  disabledFields,
  hasSaleChannel,
  visibleDrawerView,
  visibleDrawerAddNewRegister,
  inboundProcessesList,
  salesChannelList,
  documentsList,
  visibleDrawerViewMode,
  drawerIsLoading,
  documentType,
  hasEntryPermission,
  stepEntryPermissionStatus,
  isLoadingEntryPermission,
  entryPermissionActive,
  saleChannelActive,
  plateFormatType,
  vehicleProfileByPlate,
  useVehicleProfileEndpoint,
  lobbyDocumentsFetchAsync,
  lobbyProcessActive,
  lobbySaleChannelActive,
  lobbyFetchItemCheckinByVehiclePlateAsync,
  lobbyFetchVehicleProfileByPlateAsync,
  lobbyUpdateVehicleProfileByPlate,
  lobbyFetchEntryPermissionAsync,
  lobbyUpdateStepEntryPermissionStatus,
  lobbyUpdateEntryPermissionActive,
}: IDrawerForm): JSX.Element => {
  const isAfrDocumentsFlow = useAfrDocumentsFlow();
  const [formLayout, setFormLayout] = useState<LayoutType>("vertical");
  const [fieldsCount, setFieldsCount] = useState(1);
  const [visibleInvoices, setVisibleInvoices] = useState(false);
  const [visibleDocuments, setVisibleDocuments] = useState(false);
  const [plate, setPlate] = useState("");
  const [autoFillData, setAutoFillData] = useState(false);
  const [autoFillUsed, setAutoFillUsed] = useState(false);
  const [disabledFieldsFilled, setDisabledFieldsFilled] = useState<{ [key: string]: boolean }>({});
  const lastAppliedVehicleProfile = useRef<IVehicleProfileByPlate | null>();
  const pendingCheckinFleetValues = useRef<BackendFleetValues | null>(null);
  const selectedSaleChannelId = Form.useWatch("saleChannelDescription", form);
  const selectedTrailerType = Form.useWatch("trailerType", form);
  const checkinOrigin = registerActive?.checkinOrigin === CheckinOriginEnum.Integration;
  const hasCheckin = checkinRegisterExisting?.checkinOrigin === CheckinOriginEnum.Integration;
  const FLEET_CHANNEL = useTranslation({ id: "fleet_channel" });
  const TRAILER_TYPE = useTranslation({ id: "truck_type" });
  const REQUIRED_FIELD = useTranslation({ id: "required_field" });
  const FILL_IN_THE_MISSING_INFORMATION = useTranslation({ id: "fill_in_the_missing_information" });
  const EXIST_CHECK_IN_TO_THIS_PLATE = useTranslation({ id: "exist_check_in_to_this_plate" });
  const CUSTOM_PLATE_DESCRIPTION_INFORMATION = useTranslation({ id: "custom_plate_description_information" });
  const BRAZIL_MERCOSUR_PLATE_DESCRIPTION_INFORMATION = useTranslation({ id: "brazil_mercosur_plate_description_information" });
  const MEXICO_PLATE_DESCRIPTION_INFORMATION = useTranslation({ id: "mexico_plate_description_information" });

  const boxTrailer = useTranslation({ id: "box_trailer" });
  const bulkTrailer = useTranslation({ id: "bulk_trailer" });
  const curtainsider = useTranslation({ id: "curtainsider" });
  const flatbed = useTranslation({ id: "flatbed" });
  const tanker = useTranslation({ id: "tanker" });
  const lightweightCurtainsider = useTranslation({ id: "lightweight_curtainsider" });
  const others = useTranslation({ id: "fleet_channel_others" });

  const trailerTypeDescriptionById = {
    [TrailerType.BOX_TRAILER]: boxTrailer,
    [TrailerType.CURTAINSIDER]: curtainsider,
    [TrailerType.FLATBED]: flatbed,
    [TrailerType.LIGHTWEIGHT_CURTAINSIDER]: lightweightCurtainsider,
    [TrailerType.BULK_TRAILER]: bulkTrailer,
    [TrailerType.TANKER]: tanker,
    [TrailerType.OTHER]: others,
  };

  const eventType = 1;
  const autoSizeInputText = { minRows: 4, maxRows: 13 };
  const isHiddenInvoiceField = () => !visibleInvoices;
  const onFormLayoutChange = ({ layout }: { layout: LayoutType }) => {
    setFormLayout(layout);
  };

  useEffect(() => {
    lobbyUpdateVehicleProfileByPlate?.(null);
  }, [visibleDrawerAddNewRegister]);

  useEffect(() => {
    setVisibleInvoices(false);
    setAutoFillUsed(false);
    setAutoFillData(false);
    setPlate("");
    pendingCheckinFleetValues.current = null;
  }, [visibleDrawerAddNewRegister]);

  useEffect(() => {
    if (visibleDrawerAddNewRegister) {
      setDisabledFieldsFilled({});
      lobbySaleChannelActive(null);
      form.setFieldsValue({ saleChannelDescription: null });
    }
  }, [visibleDrawerAddNewRegister]);

  useEffect(() => {
    if (!visibleDrawerAddNewRegister) {
      form.resetFields();
    }
  }, [visibleDrawerAddNewRegister]);

  useEffect(() => {
    if (visibleDrawerView) {
      const processCorrelationIdActive = searchProcessCorrelationIdSelectedById(registerActive?.processId, inboundProcessesList);
      processCorrelationIdActive && lobbyDocumentsFetchAsync(processCorrelationIdActive, eventType);
      lobbyProcessActive(registerActive?.processId);
      lobbySaleChannelActive(getSaleChannelFormValue(registerActive, hasSaleChannel, salesChannelList));
      checkWhenDocumentsIsEmpty(documentsList, registerActive?.documents, form);
      setVisibleInvoices(true);
      setVisibleDocuments(true);
    }
  }, [visibleDrawerView, registerActive, inboundProcessesList, salesChannelList]);

  useEffect(() => {
    if (validationPlate.test(plate) && visibleDrawerAddNewRegister) {
      if (useVehicleProfileEndpoint) {
        lobbyFetchVehicleProfileByPlateAsync?.(plate, true);
      } else {
        lobbyFetchItemCheckinByVehiclePlateAsync?.(plate);
      }
    }
  }, [plate]);

  useEffect(() => {
    setAutoFillData(shouldShowAutoFill({ useVehicleProfileEndpoint, visibleDrawerAddNewRegister, plate, validationPlate, checkinRegisterExisting }));
  }, [checkinRegisterExisting, plate, useVehicleProfileEndpoint, visibleDrawerAddNewRegister]);

  useEffect(() => {
    if (!visibleDrawerAddNewRegister) return;

    if (vehicleProfileByPlate === null) {
      lastAppliedVehicleProfile.current = null;
      return;
    }

    if (vehicleProfileByPlate === undefined || lastAppliedVehicleProfile.current === vehicleProfileByPlate) return;

    const resolution = resolveFleetFormValues({
      currentValues: {
        saleChannelDescription: form.getFieldValue("saleChannelDescription"),
        trailerType: form.getFieldValue("trailerType"),
      },
      backendValues: vehicleProfileByPlate,
      hasSaleChannel,
      salesChannelList,
    });

    if (resolution.pending) return;

    lastAppliedVehicleProfile.current = vehicleProfileByPlate;
    if (!resolution.hasBackendValues) return;

    form.setFieldsValue(resolution.values);
    lobbySaleChannelActive(resolution.values.saleChannelDescription ?? null);
  }, [form, hasSaleChannel, lobbySaleChannelActive, salesChannelList, vehicleProfileByPlate, visibleDrawerAddNewRegister]);

  useEffect(() => {
    if (!visibleDrawerAddNewRegister || !pendingCheckinFleetValues.current) return;

    const resolution = resolveFleetFormValues({
      currentValues: {
        saleChannelDescription: form.getFieldValue("saleChannelDescription"),
        trailerType: form.getFieldValue("trailerType"),
      },
      backendValues: pendingCheckinFleetValues.current,
      hasSaleChannel,
      salesChannelList,
    });

    if (resolution.pending) return;

    pendingCheckinFleetValues.current = null;
    form.setFieldsValue(resolution.values);
    lobbySaleChannelActive(resolution.values.saleChannelDescription ?? null);
  }, [form, hasSaleChannel, lobbySaleChannelActive, salesChannelList, visibleDrawerAddNewRegister]);

  const isEditableDrawerActive = !disabledFields && (visibleDrawerAddNewRegister || (visibleDrawerView && visibleDrawerViewMode === DrawerStatusEnum.Edit));

  useEffect(() => {
    if (isEditableDrawerActive && selectedTrailerType === TrailerType.OTHER && selectedSaleChannelId !== FleetType.Others) {
      form.setFieldsValue({ trailerType: null });
    }
  }, [form, isEditableDrawerActive, selectedSaleChannelId, selectedTrailerType]);

  useEffect(() => {
    if (autoFillUsed) {
      setTimeout(() => {
        const initialCarrierValue = form.getFieldValue("carrier");
        const initialProcessDescription = form.getFieldValue("processDescription");

        setDisabledFieldsFilled({
          carrier: hasCheckin && initialCarrierValue,
          processDescription: hasCheckin && initialProcessDescription,
        });
      }, 0);
    }
  }, [form, autoFillUsed, hasCheckin, documentType]);

  useEffect(() => {
    if (visibleDrawerView && visibleDrawerViewMode === DrawerStatusEnum.Edit && registerActive?.id) {
      const requiredFields = ["carrier", "vehiclePlate", "processDescription", "trailerType"];
      if (hasSaleChannel) requiredFields.push("saleChannelDescription");

      const validationTimer = setTimeout(() => form.validateFields(requiredFields), 0);
      return () => clearTimeout(validationTimer);
    }
  }, [form, hasSaleChannel, registerActive, salesChannelList, visibleDrawerView, visibleDrawerViewMode]);

  const isFieldDisabled = (fieldName: string) => {
    return disabledFieldsFilled[fieldName];
  };

  const handleChangeProcess = (processId) => {
    lobbyProcessActive(processId);
    const processDescriptionActive = searchProcessByIdSelected(processId, inboundProcessesList);
    const processCorrelationId = searchProcessCorrelationIdSelectedById(processId, inboundProcessesList);
    form.setFieldsValue({ processDescriptionActive });
    setVisibleInvoices(true);
    lobbyDocumentsFetchAsync(processCorrelationId, eventType);
    if (!visibleDrawerView) {
      setVisibleDocuments(true);
    }
  };

  const handleChangeSalesChannel = (saleChannelId) => {
    if (saleChannelId !== FleetType.Others && form.getFieldValue("trailerType") === TrailerType.OTHER) {
      form.setFieldsValue({ trailerType: null });
    }
    lobbySaleChannelActive(saleChannelId);
    const saleChannelDescriptionActive = searchSaleChannelByIdSelected(saleChannelId, salesChannelList);
    form.setFieldsValue({ saleChannelDescriptionActive });
  };

  const handleConfirmAutoFill = () => {
    form.resetFields(["driver1", "cpf1"]);
    const backendFleetValues = {
      saleChannelId: getSaleChannelId(checkinRegisterExisting?.saleChannel),
      trailerType: checkinRegisterExisting?.trailerType,
    };
    const fleetResolution = resolveFleetFormValues({
      currentValues: {
        saleChannelDescription: form.getFieldValue("saleChannelDescription"),
        trailerType: form.getFieldValue("trailerType"),
      },
      backendValues: backendFleetValues,
      hasSaleChannel,
      salesChannelList,
    });
    const registerFormValues = { ...checkinRegisterExisting };
    delete registerFormValues.saleChannelDescription;
    delete registerFormValues.trailerType;
    form.setFieldsValue({ ...registerFormValues, ...fleetResolution.values });
    pendingCheckinFleetValues.current = fleetResolution.pending ? backendFleetValues : null;
    const processCorrelationIdActive = searchProcessCorrelationIdSelectedById(checkinRegisterExisting?.processId, inboundProcessesList);
    processCorrelationIdActive && lobbyDocumentsFetchAsync(processCorrelationIdActive, eventType);
    lobbyProcessActive(checkinRegisterExisting?.processId);
    if (!fleetResolution.pending) {
      lobbySaleChannelActive(fleetResolution.values.saleChannelDescription ?? null);
    }
    checkWhenDocumentsIsEmpty(documentsList, checkinRegisterExisting?.documents, form);
    form.setFieldsValue({ processDescription: checkinRegisterExisting?.processDescription });
    setVisibleInvoices(true);
    setVisibleDocuments(true);
    setAutoFillUsed(true);
    setAutoFillData(false);
  };

  const validationPlate = checkPlateFormatType(plateFormatType);

  const PLATE_DESCRIPTIONS: Record<IPlateFormatType, string> = {
    0: BRAZIL_MERCOSUR_PLATE_DESCRIPTION_INFORMATION,
    1: CUSTOM_PLATE_DESCRIPTION_INFORMATION,
    2: MEXICO_PLATE_DESCRIPTION_INFORMATION,
  };

  const translateDescriptionInformationPlate = (plateFormatType: IPlateFormatType): string => PLATE_DESCRIPTIONS[plateFormatType] || PLATE_DESCRIPTIONS[0];

  return (
    <Loading show={drawerIsLoading}>
      <Form style={S.FormLayout} layout={"vertical"} form={form} initialValues={{ layout: formLayout }} onValuesChange={onFormLayoutChange} name="registers">
        <S.LayoutForm hasEntryPermission={hasEntryPermission}>
          <div className="item">
            <S.FormItem
              name="carrier"
              label={`${useTranslation({ id: "shipping_company" })}*`}
              style={S.ShippingCompanyField}
              hasFeedback={!disabledFields}
              rules={[
                {
                  required: true,
                  message: REQUIRED_FIELD,
                },
              ]}
            >
              <Input maxLength={60} style={S.InputCarrier} disabled={disabledFields || isFieldDisabled("carrier") || checkinOrigin} ref={carrierRef} autoFocus />
            </S.FormItem>
            <S.PlateContainer>
              <S.FormPlateItem
                plateFormatType={plateFormatType}
                label={`${useTranslation({ id: "plate" })}*`}
                tooltip={translateDescriptionInformationPlate(plateFormatType)}
                name="vehiclePlate"
                hasFeedback={!disabledFields}
                rules={[
                  {
                    required: true,
                    message: REQUIRED_FIELD,
                  },
                  {
                    pattern: validationPlate,
                    message: useTranslation({ id: "invalid_plate" }),
                  },
                ]}
              >
                <Input
                  maxLength={maxLengthPlateFormatType(plateFormatType)}
                  data-testid="inputVehiclePlate"
                  onKeyPress={(e) => {
                    validationCharacters(e, validationSpecialCharacters);
                  }}
                  onChange={(e) => setPlate(e.target.value)}
                  style={S.InputCustomPlate(plateFormatType)}
                  disabled={disabledFields || autoFillUsed || disabledPlate}
                />
              </S.FormPlateItem>
              <Popconfirm
                placement="right"
                open={autoFillData}
                title={
                  <div>
                    {EXIST_CHECK_IN_TO_THIS_PLATE}
                    <br />
                    {FILL_IN_THE_MISSING_INFORMATION}
                  </div>
                }
                onConfirm={() => handleConfirmAutoFill()}
                showCancel={false}
                okText="Ok"
              ></Popconfirm>
            </S.PlateContainer>
            <S.FormPlateItem
              plateFormatType={plateFormatType}
              label={`${useTranslation({ id: "trailers_plates" })}`}
              tooltip={
                <>
                  {useTranslation({ id: "description_information_add_plates" })}
                  <br />
                  {translateDescriptionInformationPlate(plateFormatType)}
                </>
              }
              rules={[{ required: false }]}
              hidden={fieldsCount === 0 && visibleDrawerViewMode === DrawerStatusEnum.View}
            >
              <Form.List name="trailersPlates" initialValue={[""]}>
                {(fields, { add }, { errors }) => (
                  <AddTrailersPlates
                    fields={fields}
                    setFieldsCount={setFieldsCount}
                    fieldsCount={fieldsCount}
                    errors={errors}
                    add={add}
                    disabled={disabledFields}
                    validationPlate={validationPlate}
                    maxLength={maxLengthPlateFormatType(plateFormatType)}
                    plateFormatType={plateFormatType}
                  />
                )}
              </Form.List>
            </S.FormPlateItem>
            <S.Driver>
              <DriversInputs
                form={form}
                disabledFields={disabledFields}
                drivers={checkinRegisterExisting?.drivers && autoFillUsed ? checkinRegisterExisting?.drivers : registerActive?.drivers}
                documentType={documentType}
                isFieldDisabled={isFieldDisabled}
                setDisabledFieldsFilled={setDisabledFieldsFilled}
                disabledFieldsFilled={disabledFieldsFilled}
              />
            </S.Driver>
            <S.VehicleInfo>
              {hasSaleChannel && (
                <S.FormItem
                  data-testid="saleChannel"
                  name="saleChannelDescription"
                  label={`${FLEET_CHANNEL}*`}
                  style={S.SaleChannelFormItem}
                  hasFeedback={false}
                  rules={[{ required: true, message: REQUIRED_FIELD }]}
                >
                  <Select size="large" style={S.SaleChannelSelect} onChange={handleChangeSalesChannel} data-testid="selectSalesChannel" disabled={disabledFields}>
                    {salesChannelList?.length > 0 &&
                      salesChannelList?.map(({ description, id }, index) => (
                        <Select.Option value={id} key={index}>
                          {description}
                        </Select.Option>
                      ))}
                  </Select>
                </S.FormItem>
              )}
              <S.FormItem
                data-testid="trailerType"
                name="trailerType"
                label={`${TRAILER_TYPE}*`}
                style={S.SaleChannelFormItem}
                hasFeedback={false}
                rules={[{ required: true, message: REQUIRED_FIELD }]}
              >
                <Select size="large" style={S.SaleChannelSelect} data-testid="selectTrailerType" disabled={disabledFields}>
                  {getTrailerTypeOptions(selectedSaleChannelId, disabledFields).map(({ id }) => (
                    <Select.Option value={id} key={id}>
                      {trailerTypeDescriptionById[id]}
                    </Select.Option>
                  ))}
                </Select>
              </S.FormItem>
            </S.VehicleInfo>
            <S.FormItem label={useTranslation({ id: "observation" })} name="observation" hidden={disabledFields && !form.getFieldValue("observation")}>
              <Input.TextArea maxLength={500} autoSize={autoSizeInputText} disabled={disabledFields} />
            </S.FormItem>
          </div>
          <div className="divider" />
          <div className="item">
            <S.FormItem
              data-testid="process"
              name="processDescription"
              label={`${useTranslation({ id: "processes" })}*`}
              style={S.ProcessesSelectFormItem}
              hasFeedback={!disabledFields}
              rules={[
                {
                  required: true,
                  message: REQUIRED_FIELD,
                },
              ]}
            >
              <Select size="large" disabled={disabledFields || isFieldDisabled("processDescription") || checkinOrigin} style={S.ProcessesSelect} onChange={handleChangeProcess}>
                {inboundProcessesList?.length > 0 &&
                  inboundProcessesList?.map((process, index) => (
                    <Select.Option value={process.processId} key={index}>
                      {process.description}
                    </Select.Option>
                  ))}
              </Select>
            </S.FormItem>

            {visibleDocuments && (
              <>
                {documentsList?.map((document) => (
                  <AddDocument
                    id={document.id}
                    key={document.id}
                    hidden={isHiddenInvoiceField()}
                    label={getDocumentLabel(document.description, isAfrDocumentsFlow)}
                    fieldName={document.description}
                    disabled={disabledFields}
                    allowLetters={document.maskRules.allowLetters}
                    allowNumbers={document.maskRules.allowNumbers}
                    allowSpecialCharacters={document.maskRules.allowSpecialCharacters}
                    minCharacters={document.maskRules.minCharacters}
                    maxCharacters={document.maskRules.maxCharacters}
                    required={document.required}
                    eventType={eventType}
                    isIntegration={checkinOrigin || hasCheckin}
                    form={form}
                    visibleDrawerViewMode={autoFillUsed ? DrawerStatusEnum.Edit : visibleDrawerViewMode}
                  />
                ))}
              </>
            )}
          </div>
          {hasEntryPermission && (
            <EntryPermission
              form={form}
              documentType={documentType}
              plateFormatType={plateFormatType}
              entryPermissionActive={entryPermissionActive}
              saleChannelActive={saleChannelActive}
              isLoadingEntryPermission={isLoadingEntryPermission}
              stepEntryPermissionStatus={stepEntryPermissionStatus}
              lobbyUpdateEntryPermissionActive={lobbyUpdateEntryPermissionActive}
              lobbyFetchEntryPermissionAsync={lobbyFetchEntryPermissionAsync}
              lobbyUpdateStepEntryPermissionStatus={lobbyUpdateStepEntryPermissionStatus}
            />
          )}
          <input data-testid="change-process" onChange={(e) => handleChangeProcess(e.target.value)} style={S.FakeBtnConfirm} />
          <input data-testid="change-sale-channel" onChange={(e) => handleChangeSalesChannel(Number(e.target.value))} style={S.FakeBtnConfirm} />
        </S.LayoutForm>
      </Form>
    </Loading>
  );
};

DrawerForm.defaultProps = {
  disabledFields: false,
};

export default DrawerForm;
